import axios from 'axios';
import { getAuthToken } from '@redpanda/utils';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5004/api';
const IDENTITY_API_URL = process.env.REACT_APP_IDENTITY_SERVICE_URL || 'http://localhost:3000/api';
const SUBSCRIPTION_API_URL = process.env.REACT_APP_SUBSCRIPTION_SERVICE_URL || 'http://localhost:3001/api';

/** 帶登入 cookie＋Authorization header 的 axios 實例（HR、identity、訂閱服務共用同一個設定） */
const createAuthedClient = (baseURL: string) => {
  const client = axios.create({ baseURL, withCredentials: true });
  client.interceptors.request.use((config) => {
    const token = getAuthToken();
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
  });
  return client;
};

const api = createAuthedClient(API_URL);
export const identityApi = createAuthedClient(IDENTITY_API_URL);
const subscriptionApi = createAuthedClient(SUBSCRIPTION_API_URL);

// 列表查詢參數（後端 utils/pagination.ts：page、pageSize、search）
interface ListParams {
  pageSize?: number;
  search?: string;
  [key: string]: string | number | boolean | undefined;
}
const PAGE_SIZE = 20;
const listParams = (page: number, params: ListParams = {}) => ({ page, pageSize: PAGE_SIZE, ...params });

// ==================== 員工 ====================

export const getEmployees = (page = 1, params: ListParams = {}) => api.get('/employees', { params: listParams(page, params) });
export const getArchivedEmployees = (page = 1, params: ListParams = {}) => getEmployees(page, { ...params, archived: true });
/** 員工下拉選單：一次抓全部未封存員工 */
export const getAllEmployees = () => getEmployees(1, { pageSize: 1000 });
export const createEmployee = (data: Record<string, unknown>) => api.post('/employees', data);
export const updateEmployee = (id: string, data: Record<string, unknown>) => api.patch(`/employees/${id}`, data);
export const deleteEmployee = (id: string) => api.delete(`/employees/${id}`);
export const bulkDeleteEmployees = (ids: string[]) => api.delete('/employees/bulk', { data: { ids } });
/** 批次封存（archived: true）／恢復（archived: false），照 CRM、PM */
export const setEmployeesArchived = (ids: string[], archived: boolean) => api.patch('/employees/bulk', { ids, archived });

// ==================== 出勤 ====================

export const getAttendanceRecords = (page = 1, params: ListParams = {}) => api.get('/attendance', { params: listParams(page, params) });
/** 期間統計（YYYY-MM-DD） */
export const getAttendanceSummary = (startDate: string, endDate: string) => api.get('/attendance/summary', { params: { startDate, endDate } });
export const createAttendanceRecord = (data: Record<string, unknown>) => api.post('/attendance', data);
export const updateAttendanceRecord = (id: string, data: Record<string, unknown>) => api.patch(`/attendance/${id}`, data);
export const deleteAttendanceRecord = (id: string) => api.delete(`/attendance/${id}`);
/** 打卡：時間由伺服器決定 */
export const checkIn = (employeeId: string) => api.post('/attendance/checkin', { employeeId });
export const checkOut = (employeeId: string) => api.post('/attendance/checkout', { employeeId });

// ==================== 請假 ====================

export const getLeaveRequests = (page = 1, params: ListParams = {}) => api.get('/leave-requests', { params: listParams(page, params) });
export const createLeaveRequest = (data: Record<string, unknown>) => api.post('/leave-requests', data);
export const updateLeaveRequest = (id: string, data: Record<string, unknown>) => api.patch(`/leave-requests/${id}`, data);
export const deleteLeaveRequest = (id: string) => api.delete(`/leave-requests/${id}`);
/** 審核：核准／拒絕（限管理員） */
export const reviewLeaveRequest = (id: string, status: 'APPROVED' | 'REJECTED') => api.patch(`/leave-requests/${id}/status`, { status });
export const getLeaveQuota = (employeeId: string) => api.get(`/leave-requests/quota/${employeeId}`);

// ==================== 薪資 ====================

export const getPayrolls = (page = 1, params: ListParams = {}) => api.get('/payrolls', { params: listParams(page, params) });
export const createPayroll = (data: Record<string, unknown>) => api.post('/payrolls', data);
export const updatePayroll = (id: string, data: Record<string, unknown>) => api.patch(`/payrolls/${id}`, data);
export const deletePayroll = (id: string) => api.delete(`/payrolls/${id}`);
/** 產生某月全部員工的薪資草稿 → { created, existing[], noSetting[] } */
export const generateMonthlyPayrolls = (year: number, month: number) => api.post('/payrolls/bulk', { year, month });
/** 依薪資設定試算（不存檔）→ { amounts }（沒有薪資設定時 amounts 為 null） */
export const previewPayroll = (employeeId: string, year: number, month: number) => api.get('/payrolls/preview', { params: { employeeId, year, month } });

// ==================== 薪資設定（底薪、固定津貼、勞退自提；調薪時新增一筆） ====================

export const getSalarySettings = (employeeId: string) => api.get(`/salary-structures/employee/${employeeId}`);
export const createSalarySetting = (data: Record<string, unknown>) => api.post('/salary-structures', data);
export const deleteSalarySetting = (id: string) => api.delete(`/salary-structures/${id}`);

// ==================== 報表 ====================

export const getAttendanceReport = (startDate: string, endDate: string) => api.get('/reports/attendance', { params: { startDate, endDate } });
export const getPayrollReport = (year: number, month?: number) => api.get('/reports/payroll', { params: { year, month } });
export const getLaborInsuranceReport = (year: number, month?: number) => api.get('/reports/labor-insurance', { params: { year, month } });
export const getHRCostReport = (startYear: number, endYear: number) => api.get('/reports/hr-cost', { params: { startYear, endYear } });

// ==================== 用戶管理 API ====================

/**
 * 獲取已審核用戶
 */
export const getApprovedUsers = () => identityApi.get('/admin/users/approved');

/**
 * 獲取待審核用戶
 */
export const getPendingUsers = () => identityApi.get('/admin/users/pending');

/**
 * 更新用戶角色
 */
export const updateUserRole = (userId: string, role: string) =>
  identityApi.patch(`/admin/users/${userId}/role`, { role });

/**
 * 刪除用戶
 */
export const deleteUser = (userId: string) => identityApi.delete(`/admin/users/${userId}`);

/**
 * 審核通過用戶
 */
export const approveUser = (userId: string, role: string, config?: { allowedSystems?: string[] }) =>
  identityApi.post(`/admin/users/${userId}/approve`, { role, ...config });

/**
 * 拒絕用戶
 */
export const rejectUser = (userId: string) => identityApi.post(`/admin/users/${userId}/reject`);

// ==================== 訂閱服務 API ====================

/**
 * 獲取員工限制資訊
 */
export const getEmployeeLimitInfo = () => {
  return subscriptionApi.get(`/subscriptions/employee-limit`);
};

export default api;
